// W12 step A5: the token getter bound to a store. Quil's own file (not canonical); the canonical
// parts it feeds are `storageScope.ts` (`boundAccountGetter`, `assertScopeAccount`) and `authToken.ts`
// (`createTokenGetter`).
//
// Before W12 every Graph call asked `msalInstance.getActiveAccount()` for its token at call time, so
// a tab working on A's store could flush with B's token the moment B signed in elsewhere. Now the
// getter is built FROM the store's scope: it returns the scope's own account from MSAL's cache by id,
// never the active one. If the active account later stops being the scope's, `onAccountChanged` is
// told once (the app stops and reloads) and the getter still never hands out the other account's
// token. If the scope's account has left the cache (signed out) the getter reports
// `InteractiveSignInRequiredError`: the store's work waits for its owner.
import type { IPublicClientApplication } from '@azure/msal-browser'
import { createTokenGetter } from './authToken'
import type { TokenGetter } from './authToken'
import { assertScopeAccount, boundAccountGetter, scopedStoreName } from './storageScope'
import type { StoreScope } from './storageScope'

/** The MSAL surface the bound token getter uses (a fake in tests). */
export type BindableInstance = Pick<IPublicClientApplication, 'acquireTokenSilent' | 'getAllAccounts' | 'getActiveAccount'>

/**
 * Naming any account but the scope's throws `ScopeMismatchError` BEFORE a getter exists, so a store
 * can never be wired to a token that is not its owner's.
 */
export function bindScope(
  instance: BindableInstance,
  scope: StoreScope,
  opts: { accountId: string; scopes: string[]; onAccountChanged?: (activeAccountId: string | null) => void },
): TokenGetter {
  assertScopeAccount(scope, opts.accountId)
  return createTokenGetter({
    instance: instance as IPublicClientApplication,
    scopes: opts.scopes,
    getAccount: boundAccountGetter(scope, {
      accounts: () => instance.getAllAccounts(),
      active: () => instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null,
      ...(opts.onAccountChanged ? { onAccountChanged: opts.onAccountChanged } : {}),
    }),
  })
}

/**
 * The scope P3's listing is made under, before the store is decided: this account, not yet a store.
 * It names no database; it only says whose token the listing may carry.
 */
export function prospectiveScope(base: string, accountId: string): StoreScope {
  return { accountId, storeName: scopedStoreName(base, accountId), legacy: false }
}
